// Invite people: make a link (expiry, max uses), copy it, see and revoke active ones, or email invites.
import { useEffect, useState } from "react";
import type { Invite } from "@bloom/shared";
import { api, auth } from "../api.ts";
import { CloseButton } from "./icons.tsx";

const EXPIRY = [
  { label: "1 day", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "Never", hours: null },
] as const;

export function InviteModal({ serverId, serverName, isAdmin, verified, email, paused, onClose }: { serverId: string; serverName: string; isAdmin: boolean; verified: boolean; email: string; paused?: boolean; onClose: () => void }) {
  const [expiry, setExpiry] = useState<number | null>(24 * 7);
  const [maxUses, setMaxUses] = useState<number | null>(null);
  const [latest, setLatest] = useState<Invite | null>(null);
  const [active, setActive] = useState<Invite[]>([]);
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    if (isAdmin) await api<Invite[]>(`/servers/${serverId}/invites`).then(setActive, () => {});
  };
  useEffect(() => {
    void load();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  const create = async () => {
    setError(null);
    try {
      const inv = await api<Invite>(`/servers/${serverId}/invites`, { body: { expiresInHours: expiry, maxUses } });
      setLatest(inv);
      setCopied(false);
      void load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const sendEmails = async () => {
    setError(null);
    try {
      const sent = await api<Invite[]>(`/servers/${serverId}/invites/email`, { body: { emails: emails.split(/[\s,;]+/).filter(Boolean) } });
      setNote(`Sent ${sent.length} ${sent.length === 1 ? "invite" : "invites"}.`);
      setEmails("");
      void load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="invite-title">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 id="invite-title">Invite people to {serverName}</h2>
          <CloseButton onClick={onClose} aria-label="Close" />
        </div>
        {paused && (
          <div className="banner" style={{ borderRadius: 10 }}>
            {serverName} isn't taking new members right now, so invites won't work until {isAdmin ? "you turn that back on in server settings" : "an admin turns that back on"}.
          </div>
        )}
        {!verified ? (
          <div className="banner" style={{ borderRadius: 10 }}>
            Confirm your email ({email}) before inviting people.
            <button className="link-btn" onClick={() => auth.resendVerification(email).then(() => setNote("Sent. Check your inbox."))}>Resend email</button>
          </div>
        ) : (
          <>
            <div className="row">
              <label className="field" style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)" }}>Expires after</span>
                <select className="input" value={String(expiry)} onChange={(e) => setExpiry(e.target.value === "null" ? null : Number(e.target.value))}>
                  {EXPIRY.map((o) => <option key={o.label} value={String(o.hours)}>{o.label}</option>)}
                </select>
              </label>
              <label className="field" style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)" }}>Max uses</span>
                <select className="input" value={String(maxUses)} onChange={(e) => setMaxUses(e.target.value === "null" ? null : Number(e.target.value))}>
                  {[null, 1, 5, 10, 25].map((n) => <option key={String(n)} value={String(n)}>{n ?? "No limit"}</option>)}
                </select>
              </label>
            </div>
            <button className="btn btn-primary" onClick={create}>Make an invite link</button>
            {latest && (
              <div className="row">
                <div className="code">{latest.url}</div>
                <button className="btn btn-small" onClick={() => navigator.clipboard.writeText(latest.url).then(() => setCopied(true))}>{copied ? "Copied" : "Copy"}</button>
              </div>
            )}
            <div className="field">
              <label htmlFor="invite-emails">Or email invites (one-time links, 7 days)</label>
              <textarea id="invite-emails" className="input" style={{ height: 72, padding: 10 }} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="maya@example.com, jordan@example.com" />
            </div>
            <button className="btn" onClick={sendEmails} disabled={!emails.trim()}>Send email invites</button>
          </>
        )}
        {note && <p className="muted" style={{ margin: 0 }}>{note}</p>}
        {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
        {isAdmin && active.length > 0 && (
          <div>
            <div className="section-label" style={{ padding: "8px 0" }}>ACTIVE INVITES</div>
            {active.map((inv) => (
              <div key={inv.code} className="invite-row">
                <span style={{ flex: 1 }}>
                  <strong>{inv.email ?? inv.code}</strong>{" "}
                  <span className="muted">
                    · {inv.uses}{inv.maxUses ? `/${inv.maxUses}` : ""} used · {inv.expiresAt ? `expires ${new Date(inv.expiresAt).toLocaleDateString()}` : "never expires"}
                  </span>
                </span>
                <button className="btn btn-small" onClick={() => api(`/servers/${serverId}/invites/${inv.code}`, { method: "DELETE" }).then(load)}>Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
